/**
 * api-fetch/meetings — verbatim KanbanApi method block extracted from the
 * former api.fetch.ts monolith. Closes over the shared {@link ApiFetchContext};
 * behaviour is unchanged. Composed by spread in createKanbanFetchApi.
 */
import type {
  ActionExecution,
  ActionTemplate,
  Allocation,
  Board,
  BoardTemplate,
  BoardTemplateAuditLogPage,
  BoardTemplateIntegrityReport,
  Card,
  CardAiThread,
  CardAttachment,
  CardChecklistItemT,
  CardChecklistT,
  CardCommentT,
  CardDependency,
  CostIntelligenceSummary,
  QuoteStageSummaryRow,
  QuoteConvertOptions,
  QuoteConvertResult,
  TrelloCardDetail,
  CardScheduleContext,
  ClassificationRule,
  EnsureTemplateLaneIdsResult,
  Lane,
  BoardMembership,
  LaneMembership,
  LinkBoardToTemplateInput,
  LinkBoardToTemplateResult,
  ListAuditLogOptions,
  ListBoardTemplatesOptions,
  ListWorkflowPresetsOptions,
  BoardRelatedCounts,
  Notebook,
  NotebookActionItemT,
  NotebookListItem,
  NotebookRecordingT,
  OrgClassificationMemory,
  ReconcileBoardInput,
  ReconcileResult,
  RelatedCards,
  Tag,
  UnlinkBoardFromTemplateInput,
  WorkflowPreset,
} from '../types'

import type {
  ActionInvokeInput,
  ActionTemplateFilter,
  AllocateEmailToLaneInput,
  AllocateEmailToLaneResult,
  AllocationExplain,
  AllocationCreate,
  AllocationFilter,
  BoardCreate,
  BoardDefaultCategory,
  BoardListFilter,
  BoardSnapshot,
  BoardSummary,
  CardCreate,
  CardParseActionsResult,
  CardDependencyCreate,
  CardDependencyFilter,
  CardFilter,
  CardSearchResult,
  DueSoonFilter,
  LinkEmailToCardInput,
  LinkEmailToCardResult,
  KanbanCalendarActionExtract,
  AssignedToMeFilter,
  CardMoveInput,
  CardTeamMessage,
  CardTeamMessageListResponse,
  CardTeamsThread,
  KanbanAllocationConflict,
  KanbanApi,
  KanbanBoardVersion,
  KanbanCasOpts,
  KanbanSearchResponse,
  LaneCardCount,
  LaneCreate,
  LaneListOptions,
  InstallWorkflowPresetInput,
  InstallWorkflowPresetResult,
  PromoteLaneToBoardResult,
  SeedDefaultBoardsInput,
  SeedDefaultBoardsResult,
  TagCreate,
  TeamMemberAddInput,
  TeamMemberAddResult,
  TeamRosterSyncResult,
  TrelloCardWritebackPatch,
  TrelloCardWritebackResult,
  TrelloSyncStatusResponse,
  TrelloConnectionSettings,
  TrelloConnectionSettingsPatch,
  TrelloCardDriftList,
  TrelloCardDriftResolveResult,
  SyncDriftResolution,
} from '../api'
import { KanbanCardConflictError, KanbanNetworkError } from '../api'
import type { TrelloEmailLinkDecisionResult } from '../types'
import type {
  BoardMeeting,
  BoardMeetingListItem,
  MeetingAnalyzeResult,
  MeetingAudioPayload,
  MeetingBaselineNoteT,
  MeetingListFilter,
  MeetingLivePayload,
  MeetingNotePayload,
  MeetingSegmentIngestResult,
  MeetingSuggestion,
  MeetingSuggestionDecision,
} from '../meetingTypes'
import type { ApiFetchContext } from './context'

export function createMeetingsApi(ctx: ApiFetchContext) {
  const { _request, _trelloRequest, _casHeaders, getAuthHeaders, baseUrl, trelloApiBase, aiApiBase, reportApiBase, buildUrl } = ctx
  return {
    // Browser-independent speech-to-text. Multipart upload (NOT the
    // JSON `_request` helper) of a MediaRecorder blob to
    // POST /api/ai/transcribe/. Do NOT set Content-Type — the browser
    // sets the multipart boundary automatically when given FormData.
    transcribeAudio: async (
      blob: Blob,
      tOpts?: { language?: string; prompt?: string; filename?: string },
    ): Promise<string> => {
      const headers: Record<string, string> = { Accept: 'application/json' }
      if (getAuthHeaders) {
        const extra = await getAuthHeaders()
        Object.assign(headers, extra)
      }
      const fd = new FormData()
      // Filename extension matters: OpenAI sniffs the container from it,
      // so we mirror the recorder's mime type into a sensible name.
      fd.append('audio', blob, tOpts?.filename || 'dictation.webm')
      if (tOpts?.language) fd.append('language', tOpts.language)
      if (tOpts?.prompt) fd.append('prompt', tOpts.prompt)
      const cleanBase = aiApiBase.replace(/\/+$/, '')
      const url = `${cleanBase}/transcribe/`
      const res = await fetch(url, {
        method: 'POST',
        headers,
        body: fd,
        credentials: 'include',
      })
      if (!res.ok) {
        // Preserve the STABLE machine code (`error`) as the thrown message so
        // the UI can map it (e.g. 422 `transcription_unconfigured`). The
        // human `detail` sentence is attached separately — previously `detail`
        // OVERWROTE `error`, so the panel received an unmappable sentence and
        // silently swallowed it (the "transcription not working" report).
        let code = `http_${res.status}`
        let detail = ''
        try {
          const j = await res.json()
          if (j?.detail) detail = String(j.detail)
          if (j?.error) code = String(j.error)
        } catch {
          // ignore body parse error
        }
        const e = new Error(code) as Error & {
          code?: string
          detail?: string
          status?: number
        }
        e.code = code
        e.detail = detail
        e.status = res.status
        throw e
      }
      const j = (await res.json()) as { text?: string }
      return (j.text || '').trim()
    },

    // CRM-QUOTE-PIPELINE — convert a quote card's report.Quote into a Job.
    // Cross-base POST to /api/report/quote/{id}/convert/ (NOT the kanban
    // base). Sends no inheritance flags so the BE defaults (inherit
    // everything, provision QDrive) apply; callers may pass overrides.
    convertQuoteToJob: async (
      quoteId: string,
      options?: QuoteConvertOptions,
    ): Promise<QuoteConvertResult> => {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }
      if (getAuthHeaders) {
        const extra = await getAuthHeaders()
        Object.assign(headers, extra)
      }
      const cleanBase = reportApiBase.replace(/\/+$/, '')
      const url = `${cleanBase}/quote/${encodeURIComponent(quoteId)}/convert/`
      const res = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(options ?? {}),
        credentials: 'include',
      })
      if (!res.ok) {
        let err = `Convert to job failed (${res.status})`
        try {
          const j = await res.json()
          // The convert view returns {error} / {errors:[...]} on 400.
          if (j?.error) err = String(j.error)
          else if (Array.isArray(j?.errors) && j.errors.length) err = String(j.errors[0])
          else if (j?.detail) err = String(j.detail)
        } catch {
          // ignore body parse error — keep the status-coded message
        }
        throw new Error(err)
      }
      return (await res.json()) as QuoteConvertResult
    },

    // Board meeting mode (FEAT-2026-06-15-BOARD-MEETING-MODE). All
    // routed through the kanban `_request` helper at /api/kanban/meetings/.
    startMeeting: (input: { board: string; language?: string; title?: string }) =>
      _request<BoardMeeting>('POST', '/meetings/', input),
    ingestMeetingSegment: (
      meetingId: string,
      input: { text: string; offset_ms?: number; context_card_id?: string },
    ) =>
      _request<MeetingSegmentIngestResult>(
        'POST', `/meetings/${meetingId}/segments/`, input,
      ),
    getMeetingLive: (meetingId: string, afterSeq?: number) =>
      _request<MeetingLivePayload>(
        'GET', `/meetings/${meetingId}/live/`, undefined,
        afterSeq ? { after_seq: afterSeq } : undefined,
      ),
    analyzeMeeting: (meetingId: string) =>
      _request<MeetingAnalyzeResult>('POST', `/meetings/${meetingId}/analyze/`),
    stopMeeting: (meetingId: string) =>
      _request<BoardMeeting>('POST', `/meetings/${meetingId}/stop/`),
    uploadMeetingAudioChunk: async (
      meetingId: string,
      blob: Blob,
      seq: number,
      filename = 'meeting.webm',
    ): Promise<{ stored: boolean; key: string; seq: number }> => {
      const headers: Record<string, string> = { Accept: 'application/json' }
      if (getAuthHeaders) {
        const extra = await getAuthHeaders()
        Object.assign(headers, extra)
      }
      const fd = new FormData()
      fd.append('audio', blob, filename)
      fd.append('seq', String(seq))
      const url = buildUrl(baseUrl, `/meetings/${meetingId}/audio-chunks/`)
      const res = await fetch(url, {
        method: 'POST',
        headers,
        body: fd,
        credentials: 'include',
      })
      if (!res.ok) {
        let code = `http_${res.status}`
        let detail = ''
        try {
          const j = await res.json()
          if (j?.detail) detail = String(j.detail)
          if (j?.code) code = String(j.code)
        } catch {
          // ignore body parse error
        }
        const e = new Error(code) as Error & { code?: string; detail?: string }
        e.code = code
        e.detail = detail
        throw e
      }
      return (await res.json()) as { stored: boolean; key: string; seq: number }
    },
    // ARC-7 MR2 recordings dashboard — RBAC-fenced list + chunk playback.
    listMeetings: (filter?: MeetingListFilter) =>
      _request<BoardMeetingListItem[]>('GET', '/meetings/', undefined, {
        ...(filter?.board ? { board: filter.board } : {}),
        ...(filter?.mine ? { mine: 1 } : {}),
        ...(filter?.status ? { status: filter.status } : {}),
        ...(filter?.limit ? { limit: filter.limit } : {}),
      }),
    getMeetingAudio: (meetingId: string) =>
      _request<MeetingAudioPayload>('GET', `/meetings/${meetingId}/audio/`),
    acceptMeetingSuggestion: (meetingId: string, suggestionId: string) =>
      _request<MeetingSuggestionDecision>(
        'POST', `/meetings/${meetingId}/suggestions/${suggestionId}/accept/`,
      ),
    dismissMeetingSuggestion: (meetingId: string, suggestionId: string) =>
      _request<MeetingSuggestionDecision>(
        'POST', `/meetings/${meetingId}/suggestions/${suggestionId}/dismiss/`,
      ),
    // ARC-7 MR5 — three-question baseline note + note-line → action.
    getMeetingNote: (meetingId: string) =>
      _request<MeetingNotePayload>('GET', `/meetings/${meetingId}/note/`),
    updateMeetingNote: (
      meetingId: string,
      patch: Partial<
        Pick<MeetingBaselineNoteT, 'delivery_md' | 'done_md' | 'todo_md'>
      >,
    ) =>
      _request<MeetingNotePayload>(
        'PATCH', `/meetings/${meetingId}/note/`, patch,
      ),
    createMeetingNoteAction: (
      meetingId: string,
      input: { text: string; due_date?: string | null },
    ) =>
      _request<{ suggestion: MeetingSuggestion }>(
        'POST', `/meetings/${meetingId}/note-actions/`, input,
      ),

    // Trello write-back (qb!2757). Same direct-fetch pattern as
    // `fetchTrelloCardDetail` because the endpoint lives under the
    // /api/trello/ prefix rather than /api/kanban/. Body shape
    // mirrors `TrelloCardWritebackPatch`; backend rejects keys
    // outside its whitelist, so we don't strip here.
    updateTrelloCard: async (
      externalId: string,
      patch: TrelloCardWritebackPatch,
    ) => {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }
      if (getAuthHeaders) {
        const extra = await getAuthHeaders()
        Object.assign(headers, extra)
      }
      const cleanBase = trelloApiBase.replace(/\/+$/, '')
      const url = `${cleanBase}/cards/${encodeURIComponent(externalId)}/`
      const res = await fetch(url, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(patch),
        credentials: 'include',
      })
      if (!res.ok) {
        let err = `PATCH ${url} failed: ${res.status}`
        try {
          const j = await res.json()
          if (j?.detail) err = j.detail
        } catch {
          // ignore body parse error
        }
        throw new Error(err)
      }
      return (await res.json()) as TrelloCardWritebackResult
    },

    // qb!3096 (2026-05-26) — operator tick / cross on the
    // autonomously-written Trello-card → Email link. Same direct-
    // fetch pattern as `updateTrelloCard` because the endpoint lives
    // under the /api/trello/ prefix rather than /api/kanban/.
    //
    // 409 from the backend means the supplied `emailId` no longer
    // matches the card's current `primary_email_id` (race against a
    // re-linker pass). The caller should refetch and re-prompt; we
    // surface the BE's `detail` so the operator sees what happened.
    confirmTrelloEmailLink: async (
      externalId: string,
      emailId?: string | null,
    ) => {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }
      if (getAuthHeaders) {
        const extra = await getAuthHeaders()
        Object.assign(headers, extra)
      }
      const cleanBase = trelloApiBase.replace(/\/+$/, '')
      const url = `${cleanBase}/cards/${encodeURIComponent(externalId)}/email/confirm/`
      const res = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(emailId ? { email_id: emailId } : {}),
        credentials: 'include',
      })
      if (!res.ok) {
        let err = `POST ${url} failed: ${res.status}`
        try {
          const j = await res.json()
          if (j?.detail) err = j.detail
        } catch {
          // ignore body parse error
        }
        throw new Error(err)
      }
      return (await res.json()) as TrelloEmailLinkDecisionResult
    },

    rejectTrelloEmailLink: async (
      externalId: string,
      emailId?: string | null,
    ) => {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }
      if (getAuthHeaders) {
        const extra = await getAuthHeaders()
        Object.assign(headers, extra)
      }
      const cleanBase = trelloApiBase.replace(/\/+$/, '')
      const url = `${cleanBase}/cards/${encodeURIComponent(externalId)}/email/reject/`
      const res = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(emailId ? { email_id: emailId } : {}),
        credentials: 'include',
      })
      if (!res.ok) {
        let err = `POST ${url} failed: ${res.status}`
        try {
          const j = await res.json()
          if (j?.detail) err = j.detail
        } catch {
          // ignore body parse error
        }
        throw new Error(err)
      }
      return (await res.json()) as TrelloEmailLinkDecisionResult
    },

  }
}
